import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import YearSelector from './YearSelector';

export const DeepPermitsChart = ({ data = [] }) => {
  // Process and sort the data
  const chartData = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return [];
    
    // Create a label that combines year and quarter for sorting
    const processedData = data.map(item => ({
      ...item,
      sortKey: `${item.year}-${item.quarter.replace('Q', '')}`
    }));
    
    // Sort by year and quarter
    const sortedData = processedData.sort((a, b) => 
      a.sortKey.localeCompare(b.sortKey)
    );
    
    // Create display label (Q1 2023, etc.)
    return sortedData.map(item => ({
      ...item,
      label: `${item.quarter} ${item.year}`,
      percentageValue: parseFloat(item.percentage.toFixed(1))
    }));
  }, [data]);

  return (
    <div>
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="label" />
          <YAxis domain={[0, 100]} />
          <Tooltip 
            formatter={(value) => [`${value}%`, 'Completed On Time']}
          />
          <Legend />
          <Bar 
            dataKey="percentageValue" 
            fill="#82ca9d" 
            name="Permits Completed On Time (%)" 
          />
        </BarChart>
      </ResponsiveContainer>
      <div className="mt-4 text-sm text-gray-600">
        <p>Total permits: {chartData.reduce((sum, item) => sum + item.total, 0)}</p>
      </div>
    </div>
  );
};

/**
 * BizDynamicsChart
 * Shows business dynamics data for CT with toggle between
 * openings/closings and births/deaths views
 */
export const BizDynamicsChart = ({ data }) => {
  const [viewMode, setViewMode] = useState('openings-closings');
  const [timeFrame, setTimeFrame] = useState(20);
  
  // Filter CT data and transform into chart format
  const chartData = useMemo(() => {
    // Make sure data is an array
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    // Filter for CT data only
    const ctData = data.filter(item => item && item.region === 'CT');
    
    // Group by quarter
    const quarterGroups = ctData.reduce((groups, item) => {
      if (!groups[item.quarter]) {
        groups[item.quarter] = {};
      }
      groups[item.quarter][item.stat] = item.value;
      return groups;
    }, {});
    
    // Convert to array format for chart
    return Object.keys(quarterGroups)
      .map(quarter => ({
        quarter,
        ...quarterGroups[quarter]
      }))
      .sort((a, b) => a.quarter.localeCompare(b.quarter));
  }, [data]);
  
  // Apply time frame filter
  const filteredData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return chartData.slice(-timeFrame * 4); // Assuming 4 quarters per year
  }, [chartData, timeFrame]);
  
  // Determine which data keys to show based on view mode
  const dataKeys = useMemo(() => viewMode === 'openings-closings'
    ? [
        { key: 'openings', color: '#4CAF50', name: 'Business Openings' },
        { key: 'closings', color: '#F44336', name: 'Business Closings' }
      ]
    : [
        { key: 'births', color: '#2196F3', name: 'Business Births' },
        { key: 'deaths', color: '#FF9800', name: 'Business Deaths' }
      ], [viewMode]);
  
  // Calculate Y-axis domain with appropriate padding
  const yDomain = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [0, 100];
    
    const currentKeys = dataKeys.map(item => item.key);
    const values = filteredData.flatMap(item => 
      currentKeys.map(key => item[key]).filter(val => val !== undefined && val !== null)
    );
    
    if (values.length === 0) return [0, 100];
    
    const max = Math.max(...values);
    return [0, Math.ceil(max * 1.1)]; // Add 10% padding at the top
  }, [filteredData, dataKeys]);
  
  // If data isn't ready yet, show a loading state
  if (!data) {
    return <div>Loading business dynamics data...</div>;
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <button 
            className={`px-4 py-2 mr-2 rounded ${viewMode === 'openings-closings' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setViewMode('openings-closings')}
          >
            Openings/Closings
          </button>
          <button 
            className={`px-4 py-2 rounded ${viewMode === 'births-deaths' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setViewMode('births-deaths')}
          >
            Births/Deaths
          </button>
        </div>
          <YearSelector 
            timeFrame={timeFrame} 
            setTimeFrame={setTimeFrame} 
            options={[{ value: 5, label: "5 Years" }, { value: 20, label: "20 Years" }]} // 5 years and 20 years options
          />
      </div>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={filteredData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="quarter" />
          <YAxis domain={yDomain} />
          <Tooltip />
          <Legend />
          {dataKeys.map(({ key, color, name }) => (
            <Line 
              key={key}
              type="monotone" 
              dataKey={key} 
              stroke={color} 
              name={name} 
              activeDot={{ r: 8 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};